import React from 'react';
import clsx from 'clsx';
import { CircularProgress, Icon, MenuItem, TextField, Typography } from '@material-ui/core';
import { isN1Instance } from './formHelpers';
import { getFixedGpu, gpuFullName } from '../components/gpuModels';

const rowStyle = { display: 'flex', flexDirection: 'row', gap: '16px', alignItems: 'flex-start', marginBottom: '16px' };

// GPUs that can be ATTACHED to an N1 shape. This set is small and stable: every
// newer accelerator (A100, L4, H100, H200, MI300X, ...) is offered ONLY as a
// dedicated machine family (a2/g2/a3), never attachable to N1 — so it must not
// appear here even when the zone's accelerator-types list reports it. Used to
// intersect the live zone list down to what this shape can actually use.
const N1_ATTACHABLE_MODELS = ['T4', 'V100', 'P100', 'P4', 'K80'];

// Offline fallback for when a zone isn't chosen yet or the live gcloud lookup
// fails — the universally N1-attachable GPUs (models above; K80 is EOL so it is
// omitted). maxCards are GCP's per-instance limits for N1.
const N1_FALLBACK = [
    { value: 'nvidia-tesla-t4', label: 'NVIDIA T4', maxCards: 4 },
    { value: 'nvidia-tesla-v100', label: 'NVIDIA V100', maxCards: 8 },
    { value: 'nvidia-tesla-p100', label: 'NVIDIA P100', maxCards: 4 },
    { value: 'nvidia-tesla-p4', label: 'NVIDIA P4', maxCards: 4 },
];

/**
 * GCP accelerator (GPU) selection for a single node role (head or a compute
 * group). GCP has two GPU models and this component adapts to the selected
 * shape:
 *
 *   - N1 shapes ATTACH a GPU. We offer only the accelerator types actually
 *     available in the selected zone (fetched live via the gcloud CLI), with the
 *     per-type max card count. Falls back to the static list when a zone isn't
 *     chosen yet or the live lookup fails, so the user is never blocked.
 *   - a2 / g2 / a3 shapes ship a FIXED GPU baked into the machine type — shown
 *     read-only (nothing is submitted; Terraform derives it from the shape).
 *   - CPU shapes render nothing.
 *
 * `options` / `loading` / `zoneSelected` / `unableToVerify` come from
 * useGcpAccelerators, shared across the head node and every compute group.
 */
export default function GcpAcceleratorFields({
    instance, instanceId,
    acceleratorType, acceleratorCount,
    onTypeChange, onCountChange,
    options = [], loading = false, zoneSelected = false, unableToVerify = false,
    disabled = false, classes,
}) {
    const inputClass = classes && classes.input ? clsx(classes.input) : undefined;

    // --- Fixed-GPU (a2/g2/a3) or CPU shape ---------------------------------
    if (!isN1Instance(instanceId)) {
        const fixed = getFixedGpu(instance, 'gcp');
        if (!fixed) return null; // CPU shape — no accelerator UI
        return (
            <div
                style={{
                    display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16,
                    padding: '8px 12px', borderRadius: 6,
                    background: '#f5f3ff', border: '1px solid #ddd6fe',
                }}
            >
                <Icon style={{ color: '#7C3AED', fontSize: 20 }}>memory</Icon>
                <Typography variant="body2" style={{ color: '#5b21b6' }}>
                    Includes <strong>{fixed.count}× {fixed.model ? gpuFullName(fixed.model) : 'GPU'}</strong>
                    {' '}— built into this machine type.
                </Typography>
            </div>
        );
    }

    // --- N1 shape: attach an optional GPU -----------------------------------
    // Prefer the live zone list once a zone is chosen and the lookup succeeded;
    // otherwise use the offline fallback so selection still works. Either way,
    // intersect with N1_ATTACHABLE_MODELS so shape-incompatible GPUs (A100, L4,
    // H100, TPUs, ...) that merely EXIST in the zone are never offered here.
    const useStatic = !zoneSelected || unableToVerify;
    const dynamicOptions = options
        .filter((o) => N1_ATTACHABLE_MODELS.indexOf(o.model) !== -1)
        .map((o) => ({
            value: o.name,
            label: o.description + (o.maxCards ? ` (up to ${o.maxCards})` : ''),
            maxCards: o.maxCards,
        }));
    const list = useStatic ? N1_FALLBACK : dynamicOptions;

    const selected = list.find((o) => o.value === acceleratorType);
    const maxCards = (selected && selected.maxCards) || 8;
    const inList = Boolean(selected);
    // A previously-selected GPU that this zone doesn't offer (e.g. after
    // changing zones on an edit form) — flag it but keep it selectable. Only
    // meaningful once the live list has loaded (not during loading / fallback).
    const selectedMissing = Boolean(acceleratorType && !loading && !useStatic && !inList);

    let helper = ' ';
    if (loading) helper = 'Loading available GPUs…';
    else if (!zoneSelected) helper = 'GPUs attachable to N1 — select a zone to confirm availability there.';
    else if (unableToVerify) helper = 'GPUs attachable to N1 — live zone availability could not be verified.';
    else if (dynamicOptions.length === 0) helper = 'No attachable GPUs for this shape in this zone.';
    else if (selectedMissing) helper = 'This GPU is not attachable to this shape in the selected zone.';

    return (
        <div style={rowStyle}>
            <TextField
                select label="Accelerator (GPU)" variant="outlined"
                className={inputClass} style={{ flex: 1 }}
                value={acceleratorType || ''}
                onChange={(e) => onTypeChange(e.target.value || null)}
                disabled={disabled || loading}
                helperText={helper}
                error={selectedMissing}
                SelectProps={{ MenuProps: { PaperProps: { style: { maxHeight: 300 } } } }}
                InputProps={loading ? { endAdornment: <CircularProgress size={18} style={{ marginRight: 24 }} /> } : undefined}
            >
                <MenuItem value=""><em>None</em></MenuItem>
                {list.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
                {/* Keep any out-of-list selection rendered so the value always
                    has a matching item (avoids MUI's out-of-range warning) and
                    edit forms don't silently drop it. */}
                {acceleratorType && !inList && <MenuItem value={acceleratorType}>{acceleratorType}</MenuItem>}
            </TextField>
            <TextField
                label="GPU Count" type="number" variant="outlined"
                className={inputClass} style={{ width: '120px' }}
                value={acceleratorCount || 0}
                onChange={(e) => onCountChange(parseInt(e.target.value, 10) || 0)}
                disabled={disabled || !acceleratorType}
                inputProps={{ min: 0, max: maxCards }}
                helperText={acceleratorType ? `Max ${maxCards}` : ' '}
            />
        </div>
    );
}
